import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, TrendingUp, Users, BarChart3, ArrowRight, CheckCircle, Globe, Zap, Shield, Star, Building2, Rocket, Code, Target, HelpingHand, MapPin, Share2, Presentation, Store } from 'lucide-react';
import ConnectWithUs from './components/ConnectWithUs';
import StartTodayButton from './components/StartTodayButton';

function App() {
  const [hoveredSolution, setHoveredSolution] = useState(null);

  const customerTypes = [
    {
      name: 'Foreign DTC Brands Launching in Americas',
      href: '/foreign-dtc-americas',
      icon: Globe,
      description: 'Growth-stage consumer brands expanding from Europe, APAC, or MENA into North American storefronts, marketplaces, and wholesale channels.'
    },
    {
      name: 'Americas (US, Canada, LATAM) DTC Brands Going Global',
      href: '/americas-global',
      icon: Rocket,
      description: 'US and Canadian founders moving into Mexico, LATAM, and India with localized go-to-market, compliance, and fulfillment roadmaps.'
    },
    {
      name: 'Manufacturers (Asia, LATAM) Launching DTC Brands in the Americas',
      href: '/manufacturers-dtc',
      icon: Building2,
      description: 'OEMs and contract manufacturers building direct-to-consumer labels, omnichannel distribution, and owned data infrastructure.'
    }
  ];

  const solutionsDropdown = [
    { name: 'New Market Entry', href: '/solutions/new-market-entry', icon: Globe },
    { name: 'Online Sales Acceleration', href: '/solutions/online-sales', icon: TrendingUp },
    { name: 'Boutique and Specialty Retail Sales', href: '/solutions/boutique-retail', icon: Star },
    { name: 'Big Box Retail Sales', href: '/solutions/big-box-retail', icon: Building2 },
    { name: 'Native Branding', href: '/solutions/native-branding', icon: Zap },
    { name: 'Influencer Marketing', href: '/solutions/influencer-marketing', icon: Users },
    { name: 'Supply Chain Design and Setup', href: '/solutions/supply-chain', icon: Package },
    { name: 'Tariff Engineering', href: '/solutions/tariff-engineering', icon: Shield },
    { name: 'Product Market Fit', href: '/solutions/product-market-fit', icon: Target },
  { name: 'B2B Sales Acceleration', href: '/solutions/b2b-sales', icon: HelpingHand },
    { name: 'Cluster-based Marketing', href: '/solutions/cluster-marketing', icon: MapPin },
    { name: 'Social Media Marketing', href: '/solutions/social-media', icon: Share2 },
    { name: 'Trade Show Representation', href: '/solutions/trade-shows', icon: Presentation },
    { name: 'Market Center Entry', href: '/solutions/market-centers', icon: Store }
  ];

  const marketsDropdown = [
    { name: 'United States', href: '/market-us', flag: '🇺🇸' },
    { name: 'Canada', href: '/market-canada', flag: '🇨🇦' },
    { name: 'Mexico and LATAM', href: '/market-latam', flag: '🇲🇽' },
    { name: 'India', href: '/market-india', flag: '🇮🇳' }
  ];

  const mainNav = [
    { name: 'Technology', href: 'https://tech.motifunbound.com', external: true },
    { name: 'Blog', href: '/blog' }
  ];

  const impactStats = [
    {
      value: '4',
      label: 'Regional launch hubs',
      helper: 'United States, Canada, Mexico & LATAM, India'
    },
    {
      value: '250+',
      label: 'Retail & marketplace lanes',
      helper: 'Big box, boutique, trade show, and marketplace access'
    },
    {
      value: '14',
      label: 'Specialized services',
      helper: 'Strategy, channel build, supply chain, and brand ops'
    },
    {
      value: '6',
      label: 'AI modules',
      helper: 'Forecasting, partner scoring, and compliance automation'
    }
  ];

  const marketFocus = [
    {
      name: 'United States',
      flag: '🇺🇸',
      description: 'Category buyer access, omnichannel launch playbooks, and compliance for the world\'s most competitive market.',
      focusAreas: ['Big box onboarding', 'Strategic trade show programs', 'Marketplace dominance'],
      href: '/market-us'
    },
    {
      name: 'Canada',
      flag: '🇨🇦',
      description: 'Bilingual storytelling and cross-border logistics powered by regional retail partners and marketplaces.',
      focusAreas: ['Big Box Retail with Walmart, Homesense and Winners', 'Amazon.ca acceleration', 'Cross-border fulfillment'],
      href: '/market-canada'
    },
    {
      name: 'Mexico & LATAM',
      flag: '🇲🇽',
      description: 'Nearshoring manufacturing combined with market entry into Mexico City, Bogotá, and São Paulo retail ecosystems.',
      focusAreas: ['USMCA tariff engineering', 'Marketplace localization', 'Regional distributor programs'],
      href: '/market-latam'
    },
    {
      name: 'India',
      flag: '🇮🇳',
      description: 'Hyper-localized branding, marketplace dominance, and partner selection for the world\'s fastest-growing consumer market.',
      focusAreas: ['Flipkart & Amazon India growth', 'Native branding & creative', 'Regulatory and compliance navigation'],
      href: '/market-india'
    }
  ];

  const solutionSpotlights = [
    {
      title: 'New Market Entry',
      summary: 'Launch in a new region with a sequenced 90-day plan spanning compliance, channel build, and activation.',
      href: '/solutions/new-market-entry',
      icon: Globe
    },
    {
      title: 'Online Sales Acceleration',
      summary: 'Build high-performing marketplace flywheels across Amazon, Walmart, Etsy, and Mercado Libre.',
      href: '/solutions/online-sales',
      icon: TrendingUp
    },
    {
      title: 'Boutique and Specialty Retail Sales',
      summary: 'Build relationships with independent retailers and specialty shops for authentic brand positioning.',
      href: '/solutions/boutique-retail',
      icon: Star
    },
    {
      title: 'Big Box Retail Sales',
      summary: 'Navigate category management and secure shelf space in major retail chains.',
      href: '/solutions/big-box-retail',
      icon: Building2
    },
    {
      title: 'Native Branding',
      summary: 'Create culturally resonant brand stories that connect with local audiences.',
      href: '/solutions/native-branding',
      icon: Zap
    },
    {
      title: 'Influencer Marketing',
      summary: 'Partner with local influencers to build authentic brand awareness and drive conversions.',
      href: '/solutions/influencer-marketing',
      icon: Users
    },
    {
      title: 'Supply Chain Design and Setup',
      summary: 'Engineer resilient nearshored supply chains with visibility, capital efficiency, and OTIF performance.',
      href: '/solutions/supply-chain',
      icon: Package
    },
    {
      title: 'Tariff Engineering',
      summary: 'Optimize product classification and sourcing strategies to minimize duty costs.',
      href: '/solutions/tariff-engineering',
      icon: Shield
    },
    {
      title: 'Product Market Fit',
      summary: 'Validate demand and refine positioning before full market launch.',
      href: '/solutions/product-market-fit',
      icon: Target
    },
    {
      title: 'B2B Sales Acceleration',
      summary: 'Institutionalize founder-led sales with revenue operations, deal rooms, and channel partnerships.',
      href: '/solutions/b2b-sales',
      icon: HelpingHand
    },
    {
      title: 'Cluster-based Marketing',
      summary: 'Target high-density customer segments with localized campaigns for efficient market penetration.',
      href: '/solutions/cluster-marketing',
      icon: MapPin
    },
    {
      title: 'Social Media Marketing',
      summary: 'Build engaged communities and drive traffic through platform-specific content strategies.',
      href: '/solutions/social-media',
      icon: Share2
    },
    {
      title: 'Trade Show Representation',
      summary: 'Execute 10-30-60 meeting pipelines that convert industry events into purchase orders.',
      href: '/solutions/trade-shows',
      icon: Presentation
    },
    {
      title: 'Market Center Entry',
      summary: 'Secure permanent showrooms and staff them with experienced reps in Dallas, Atlanta, Las Vegas, and LA.',
      href: '/solutions/market-centers',
      icon: Store
    }
  ];

  const techHighlights = [
    {
      title: 'Expansion OS',
      description: 'Timeline, workstream, and readiness dashboards keep every stakeholder synchronized from factory to storefront.',
      icon: Code
    },
    {
      title: 'Partner Graph',
      description: 'AI matching scores retailer, distributor, and influencer partners based on product fit and performance history.',
      icon: BarChart3
    },
    {
      title: 'Trade Compliance Bot',
      description: 'Automated tariff classification, HS validation, and document prep for USMCA, GST, and import duties.',
      icon: Shield
    },
    {
      title: 'Demand Signals Engine',
      description: 'Marketplace, social, and POS data combine to forecast velocity and inform production and allocation.',
      icon: Target
    }
  ];

  const partnerNetwork = [
    {
      title: 'Enterprise & Big Box Retail',
      description: 'Warm introductions to off-price, department store, and mass retail buyers ready for newness.',
      logos: [
        { name: 'TJ Maxx', src: 'https://logo.clearbit.com/tjmaxx.tjx.com' },
        { name: 'Marshalls', src: 'https://logo.clearbit.com/marshalls.com' },
        { name: 'Walmart Canada', src: 'https://logo.clearbit.com/walmart.ca' }
      ]
    },
    {
      title: 'Trade Shows & Events',
      description: 'From MAGIC to NRF, our team books meetings, staffs booths, and converts floor traffic into orders.',
      logos: [
        { name: 'MAGIC', src: 'https://logo.clearbit.com/magicfashionevents.com' },
        { name: 'NRF', src: 'https://logo.clearbit.com/nrf.com' },
        { name: 'NY NOW', src: 'https://logo.clearbit.com/nynow.com' }
      ]
    },
    {
      title: 'Market Centers',
      description: 'Permanent showrooms and pop-ups inside AmericasMart, Dallas Market Center, and Las Vegas Market.',
      logos: [
        { name: 'Dallas Market Center', src: 'https://logo.clearbit.com/dallasmarketcenter.com' },
        { name: 'Las Vegas Market', src: 'https://logo.clearbit.com/lasvegasmarket.com' },
        { name: 'AmericasMart', src: 'https://logo.clearbit.com/americasmart.com' }
      ]
    },
    {
      title: 'Digital Marketplaces',
      description: 'Dedicated account teams across Amazon, Walmart, Etsy, Mercado Libre, and regional platforms.',
      logos: [
        { name: 'Amazon', src: 'https://logo.clearbit.com/amazon.com' },
        { name: 'Walmart.com', src: 'https://logo.clearbit.com/walmart.com' },
        { name: 'Etsy', src: 'https://logo.clearbit.com/etsy.com' }
      ]
    }
  ];

  // All partner logos for carousel
  const allPartnerLogos = [
    { name: 'TJ Maxx', src: 'https://logo.clearbit.com/tjmaxx.tjx.com' },
    { name: 'Marshalls', src: 'https://logo.clearbit.com/marshalls.com' },
    { name: 'Walmart Canada', src: 'https://logo.clearbit.com/walmart.ca' },
    { name: 'Target', src: 'https://logo.clearbit.com/target.com' },
    { name: 'Costco', src: 'https://logo.clearbit.com/costco.com' },
    { name: 'Amazon', src: 'https://logo.clearbit.com/amazon.com' },
    { name: 'Walmart', src: 'https://logo.clearbit.com/walmart.com' },
    { name: 'Etsy', src: 'https://logo.clearbit.com/etsy.com' },
    { name: 'MAGIC', src: 'https://logo.clearbit.com/magicfashionevents.com' },
    { name: 'NRF', src: 'https://logo.clearbit.com/nrf.com' },
    { name: 'NY NOW', src: 'https://logo.clearbit.com/nynow.com' },
    { name: 'Dallas Market Center', src: 'https://logo.clearbit.com/dallasmarketcenter.com' },
    { name: 'Las Vegas Market', src: 'https://logo.clearbit.com/lasvegasmarket.com' },
    { name: 'AmericasMart', src: 'https://logo.clearbit.com/americasmart.com' },
    { name: 'Mercado Libre', src: 'https://logo.clearbit.com/mercadolibre.com' },
    { name: 'Shopify', src: 'https://logo.clearbit.com/shopify.com' }
  ];

  const thoughtLeadership = [
    {
      title: 'The Future of DTC: Why 2025 is the Year of Profitability Over Growth',
      date: 'November 8, 2025',
      readTime: '5 min read',
      excerpt: 'How leading brands are pivoting from blitzscaling to sustainable unit economics and omnichannel scale.'
    },
    {
      title: 'Nearshoring Revolution: Mexico Becomes the Manufacturing Hub of the Americas',
      date: 'November 5, 2025',
      readTime: '10 min read',
      excerpt: 'Tariff relief, agility, and quality control make Mexico the default choice for North American expansion.'
    },
    {
      title: 'Why Omnichannel Wins: The Retail Playbook for 2025',
      date: 'November 1, 2025',
      readTime: '9 min read',
      excerpt: 'Retail shelves and digital funnels now work together—here is how modern DTC founders blend both.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <main>
        <section className="bg-gradient-to-br from-[#000000] via-[#1a5f60] to-[#38A3A5] text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-14 items-start">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-1 rounded-full text-sm font-semibold tracking-wide uppercase">
                  <span className="h-2 w-2 rounded-full bg-[#FFD965]"></span>
                  Global DTC Expansion Partner
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-6 mb-6 leading-tight">
                  Cross Borders. Build Brands. Own Markets.
                </h1>
                <div className="lg:hidden rounded-3xl overflow-hidden shadow-2xl mb-8">
                  <img 
                    src="/images/hero-illustration.png" 
                    alt="Motif Unbound Global Expansion" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-base md:text-lg text-white/80 max-w-xl">
                  Whether you're a foreign brand entering the Americas, a US company going global, or a manufacturer launching direct-to-consumer—Motif Unbound delivers end-to-end execution. We combine specialized services, proven market access, trusted partner networks, and industry thought leadership to turn expansion plans into revenue at scale.
                </p>
                <div className="flex flex-wrap gap-4 mt-10">
                  <StartTodayButton variant="large" />
                  <a
                    href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
                    className="inline-flex items-center gap-2 bg-[#FFD965] px-6 py-3 lg:px-8 lg:py-4 rounded-lg font-semibold text-[#000000] transition-all hover:bg-[#E5B84A]"
                  >
                    Schedule a Consult
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
              <div className="hidden lg:block rounded-3xl overflow-hidden shadow-2xl order-1 lg:order-2">
                <img 
                  src="/images/hero-illustration.png" 
                  alt="Motif Unbound Global Expansion" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-[#000000] via-[#1a5f60] to-[#38A3A5] text-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
              {impactStats.map((stat, idx) => (
                <div key={idx} className="bg-gradient-to-br from-[#FFD965] to-[#E5B84A] rounded-2xl p-5">
                  <div className="text-3xl font-bold text-[#000000]">{stat.value}</div>
                  <div className="text-sm uppercase tracking-wide text-[#000000]/70 mt-1">{stat.label}</div>
                  <p className="text-sm text-[#000000]/70 mt-3">{stat.helper}</p>
                </div>
              ))}
            </div>
            <ConnectWithUs variant="inline" />
          </div>
        </section>

        <section id="who-we-serve" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <h2 className="text-4xl font-bold text-[#000000]">Who We Serve</h2>
                <p className="text-lg text-gray-600 mt-3 max-w-2xl">
                  Expansion-minded founders and operators who need a global commercialization engine—not just consultants or agencies.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customerTypes.map((segment, idx) => (
                <Link
                  key={idx}
                  to={segment.href}
                  className="group bg-gray-50 border border-gray-200 hover:border-[#38A3A5] rounded-2xl p-8 transition-all flex flex-col h-full hover:shadow-lg"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#38A3A5]/10 text-[#38A3A5] flex items-center justify-center mb-6">
                    <segment.icon size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#000000] mb-3">{segment.name}</h3>
                  <p className="text-sm text-gray-600 flex-1">{segment.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#38A3A5] mt-6">
                    Explore use cases
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-10 text-center">
              <StartTodayButton variant="large" />
            </div>
          </div>
        </section>

        <section id="markets" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-[#000000]">We don't just do compliance and checklists. We unlock growth at scale.</h2>
              <p className="text-lg text-gray-600 mt-3 max-w-2xl">
                From the United States to Canada, Mexico & LATAM, and India, our teams deliver regulatory, localization, and multi-channel rollouts designed to build market leadership, not just presence.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {marketFocus.map((market, idx) => (
                <div key={idx} className="bg-white border border-[#38A3A5]/20 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{market.flag}</span>
                    <h3 className="text-xl font-semibold text-[#000000]">{market.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">{market.description}</p>
                  <ul className="space-y-3 mb-6">
                    {market.focusAreas.map((area, areaIdx) => (
                      <li key={areaIdx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle size={16} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={market.href}
                    className="inline-flex items-center gap-2 text-[#38A3A5] hover:text-[#2d8285] font-semibold text-sm"
                  >
                    View {market.name} Market Intelligence
                    <ArrowRight size={16} />
                  </Link>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <StartTodayButton variant="large" />
            </div>
          </div>
        </section>

        <section id="solutions" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-[#000000]">Market Entry & Growth Playbooks</h2>
              <p className="text-lg text-gray-600 mt-3 max-w-2xl">
                Fourteen specialized services pair with our Expansion OS to take you from market diligence to renewable revenue across retail, wholesale, and digital channels. Hover over each card to learn more.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {solutionSpotlights.map((solution, idx) => {
                const isActive = hoveredSolution === idx;

                return (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onMouseEnter={() => setHoveredSolution(idx)}
                    onMouseLeave={() => setHoveredSolution(null)}
                    onFocus={() => setHoveredSolution(idx)}
                    onBlur={() => setHoveredSolution(null)}
                    onClick={() => setHoveredSolution(isActive ? null : idx)}
                    className="relative h-48 cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38A3A5]"
                  >
                    {/* Front of card - consistent yellow */}
                    <div
                      className={`absolute inset-0 rounded-xl p-6 flex flex-col items-center justify-center text-center text-[#000000] shadow-lg transition-opacity duration-300 bg-gradient-to-br from-[#FFD965] to-[#E5B84A] ${isActive ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                    >
                      <solution.icon size={32} className="mb-3" />
                      <h3 className="text-base lg:text-sm font-bold leading-tight">{solution.title}</h3>
                    </div>

                    {/* Back of card */}
                    <div
                      className={`absolute inset-0 bg-white border-2 border-[#38A3A5] rounded-xl p-4 flex flex-col justify-between shadow-lg transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    >
                      <p className="text-sm lg:text-xs text-gray-700 leading-snug">{solution.summary}</p>
                      <Link
                        to={solution.href}
                        className="inline-flex items-center justify-center gap-1 text-sm lg:text-xs font-semibold mt-2 text-[#38A3A5] hover:text-[#2d8284]"
                      >
                        Learn More
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-10 text-center">
              <StartTodayButton variant="large" />
            </div>
          </div>
        </section>

        <section id="tech" className="py-16 bg-[#2d8284] text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <h2 className="text-4xl font-bold">Technology That Orchestrates Expansion</h2>
                <p className="text-lg text-white/80 mt-3 max-w-2xl">
                  The Motif Unbound Expansion OS keeps every stakeholder aligned—combining planning, partner matching, compliance automation, and revenue intelligence.
                </p>
              </div>
              <StartTodayButton variant="large" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {techHighlights.map((feature, idx) => (
                <div key={idx} className="bg-gradient-to-br from-[#FFD965] to-[#E5B84A] rounded-2xl p-6 h-full hover:brightness-105 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#000000]/10 text-[#000000] flex items-center justify-center mb-6">
                    <feature.icon size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#000000] mb-3">{feature.title}</h3>
                  <p className="text-sm text-[#000000]/80">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="partners" className="py-16 bg-[#38A3A5]/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <h2 className="text-4xl font-bold text-[#000000]">Partner Network that Opens Doors</h2>
                <p className="text-lg text-gray-600 mt-3 max-w-2xl">
                  Tap into curated relationships across retail, trade shows, market centers, and digital marketplaces—ready to accelerate your launch.
                </p>
              </div>
              <a
                href="https://partner.motifunbound.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#BB3E00] font-semibold hover:text-[#8B2E00]"
              >
                Join the partner ecosystem
                <ArrowRight size={16} />
              </a>
            </div>

            {/* Infinite scrolling logo carousel */}
            <div className="relative mb-12 overflow-hidden rounded-3xl bg-white py-12">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white via-white/60 to-transparent"></div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white via-white/60 to-transparent"></div>
              <div className="flex items-center gap-10 animate-scroll">
                {/* First set of logos */}
                {allPartnerLogos.map((logo, idx) => (
                  <div
                    key={`logo-1-${idx}`}
                    className="flex h-20 w-48 shrink-0 items-center justify-center px-6"
                  >
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className="max-h-14 w-full object-contain opacity-70 transition-opacity hover:opacity-100"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
                {/* Duplicate set for seamless loop */}
                {allPartnerLogos.map((logo, idx) => (
                  <div
                    key={`logo-2-${idx}`}
                    className="flex h-20 w-48 shrink-0 items-center justify-center px-6"
                  >
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className="max-h-14 w-full object-contain opacity-70 transition-opacity hover:opacity-100"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {partnerNetwork.map((segment, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 h-full shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="text-xl font-semibold text-[#000000] mb-3">{segment.title}</h3>
                  <p className="text-sm text-gray-600 mb-6">{segment.description}</p>
                  <div className="flex flex-wrap gap-4 items-center">
                    {segment.logos.map((logo) => (
                      <div key={logo.name} className="h-10 flex items-center">
                        <img
                          src={logo.src}
                          alt={logo.name}
                          className="h-10 w-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <StartTodayButton variant="large" />
            </div>
          </div>
        </section>

        <section id="insights" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <h2 className="text-4xl font-bold text-[#000000]">Insights from the Motif Unbound Lab</h2>
                <p className="text-lg text-gray-600 mt-3 max-w-2xl">
                  We publish deep dives on profitability, nearshoring, omnichannel expansion, and DTC growth so you can stay ahead of the market.
                </p>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[#BB3E00] font-semibold hover:text-[#8B2E00]"
              >
                Read all articles
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {thoughtLeadership.map((post, idx) => {
                return (
                  <Link
                    key={idx}
                    to="/blog"
                    className="group bg-white border border-gray-200 rounded-2xl p-8 transition-all hover:border-[#38A3A5] flex flex-col h-full"
                  >
                    <div className="text-xs uppercase tracking-wide text-[#BB3E00] font-semibold">Featured Insight</div>
                    <h3 className="text-xl font-semibold text-[#000000] mt-4 mb-3">{post.title}</h3>
                    <p className="text-sm text-gray-600 flex-1">{post.excerpt}</p>
                    <div className="mt-6 text-xs text-gray-500">
                      {post.date} • {post.readTime}
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#38A3A5] mt-6">
                      Continue reading
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Connect with us section */}
            <ConnectWithUs />
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#000000] to-[#38A3A5] text-white py-20">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Let's Launch Your Next Market</h2>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              Whether you are preparing your first US rollout or expanding across LATAM and India, Motif Unbound delivers the operators, technology, and partner network to deliver revenue faster.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <a
                href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
                className="inline-flex items-center gap-2 bg-[#FFD965] text-[#000000] px-8 py-4 rounded-lg font-semibold transition-all hover:bg-[#f5cc4d]"
              >
                Schedule a Consult
                <ArrowRight size={18} />
              </a>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/30 px-8 py-4 rounded-lg font-semibold text-white hover:bg-white/20"
              >
                Explore Thought Leadership
                <ArrowRight size={18} />
              </Link>
            </div>
            <p className="mt-6 text-white/60 text-sm">Launch assessments within 10 business days • Custom playbooks for every channel</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#000000] text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-semibold text-white mb-4">Who We Serve</h4>
              <ul className="space-y-2 text-sm">
                {customerTypes.map((item, idx) => (
                  <li key={idx}>
                    <Link to={item.href} className="hover:text-[#38A3A5] transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm">
                {solutionsDropdown.map((item, idx) => (
                  <li key={idx}>
                    <Link to={item.href} className="hover:text-[#38A3A5] transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://tech.motifunbound.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#38A3A5] transition-colors">Technology</a></li>
                <li><a href="https://partner.motifunbound.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#38A3A5] transition-colors">Partners</a></li>
                <li><Link to="/blog" className="hover:text-[#38A3A5] transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <ConnectWithUs variant="footer" />
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center">
            © 2025 Motif Unbound. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;